import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNavigate } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { useBannerContext } from '@xing-com/crate-jobs-components-banner';
import { getDialCodeFromCountryCode } from '@xing-com/crate-jobs-domain-apply-helpers';
import {
  useLazyGetUserRecentCvs,
  useUserEmailWithPhoneFallback,
} from '@xing-com/crate-jobs-domain-apply-hooks';
import type { ApplicationType } from '@xing-com/crate-jobs-helpers';
import { isValidPhoneNumberForCountry } from '@xing-com/crate-jobs-helpers';
import {
  useGetJobApplicationType,
  useGetLazyNeffiScore,
} from '@xing-com/crate-jobs-hooks';
import { useExperiment } from '@xing-com/hub';
import { IconButterflyMono } from '@xing-com/icons';

import { useRedirectToLoginWithJobDestUrl } from './use-redirect-to-login-with-job-dest-url';
import type { ApplyTrackingProps } from './use-track-apply-intention';
import { useTrackApplyIntention } from './use-track-apply-intention';

const QUICK_APPLY_NEFFI_SCORE_THRESHOLD = 100;

type InstantApplyButtonState = {
  icon: typeof IconButterflyMono;
  to: undefined;
  onClick: (trackingProps?: ApplyTrackingProps) => void;
  loading: boolean;
};

type ApplyButtonStateProviderProps = {
  applicationType?: ApplicationType;
  jobId: string;
  jobSlug: string;
};

const ApplyButtonStateContext = createContext<InstantApplyButtonState>({
  icon: IconButterflyMono,
  to: undefined,
  onClick: () => ({}),
  loading: false,
});

export const ApplyButtonStateProvider: React.FC<
  PropsWithChildren<ApplyButtonStateProviderProps>
> = ({ applicationType = 'JobXingApplication', children, jobId, jobSlug }) => {
  const isXingApplyFlow = applicationType === 'JobXingApplication';
  const isPhoneRequired = useExperiment('ABACUS-559') === 'B';
  const { isLoggedIn } = useLoginState();
  const navigate = useNavigate();
  const {
    getJobApplicationType,
    isLoading: isLoadingApplicationType,
    data,
    error,
  } = useGetJobApplicationType();
  const {
    getLazyNeffiScore,
    normalizedNeffiScore,
    isLoading: isLoadingNeffi,
  } = useGetLazyNeffiScore();
  const {
    getUserRecentCvs,
    userRecentCvs,
    isLoading: isLoadingCvs,
  } = useLazyGetUserRecentCvs();
  const { showErrorBanner } = useBannerContext();
  const redirectToLogin = useRedirectToLoginWithJobDestUrl();
  const { getSearchParameter } = useSearchParameter();
  const trackApplyIntention = useTrackApplyIntention({
    applicationType,
    jobId,
  });
  const [isApplyClickInProgress, setIsApplyClickInProgress] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const shouldLoadUserPhone =
    isApplyClickInProgress && isLoggedIn && isXingApplyFlow && isPhoneRequired;
  const {
    isUserEmailLoading,
    userFormData: { userPhone },
  } = useUserEmailWithPhoneFallback(shouldLoadUserPhone);
  const parsedUserPhone = userPhone?.parsedNumber;
  const isRequiredUserPhoneMissingOrInvalid =
    isPhoneRequired &&
    (!userPhone?.fullNumber ||
      !parsedUserPhone ||
      !isValidPhoneNumberForCountry(
        parsedUserPhone.number,
        parsedUserPhone.countryCode,
        getDialCodeFromCountryCode(parsedUserPhone.countryCode)
      ));
  const isLoadingUserPhone = shouldLoadUserPhone && isUserEmailLoading;
  const resetAndShowError = useCallback(
    (errorCopy: string) => {
      showErrorBanner(errorCopy);
      setIsApplyClickInProgress(false);
    },
    [showErrorBanner]
  );

  useEffect(() => {
    if (isLoadingApplicationType) {
      return;
    }

    if (isApplyClickInProgress) {
      if (error) {
        resetAndShowError('JOBS_ERROR_SOMETHING_WENT_WRONG');
        return;
      }
      const {
        isVisibleJob,
        applicationType: apiApplicationType,
        isLoApplyEnabled,
      } = data;

      if (!isVisibleJob) {
        resetAndShowError('JOBS_JOB_DETAILS_XING_APPLY_DEACTIVATED_ERROR');
        return;
      }
      if (applicationType !== apiApplicationType) {
        resetAndShowError('JOBS_JOB_DETAILS_XING_APPLY_TYPE_ERROR');
        return;
      }

      if (
        isLoadingNeffi ||
        isLoadingCvs ||
        isLoadingUserPhone ||
        isRedirecting
      ) {
        return;
      }

      setIsRedirecting(true);

      const fixedParams = new URLSearchParams();
      const ijt = getSearchParameter('ijt');
      if (ijt) {
        fixedParams.set('ijt', ijt);
      }
      const applyPath = `${isXingApplyFlow ? '/jobs/apply' : '/jobs/custom-apply'}/${jobSlug}?${fixedParams.toString()}`;

      if (isLoggedIn) {
        if (
          isXingApplyFlow &&
          !isRequiredUserPhoneMissingOrInvalid &&
          (userRecentCvs.length > 0 ||
            normalizedNeffiScore >= QUICK_APPLY_NEFFI_SCORE_THRESHOLD)
        ) {
          navigate(`/jobs/quick-apply/${jobSlug}?${fixedParams.toString()}`);
        } else {
          navigate(applyPath);
        }
      } else if (isLoApplyEnabled) {
        navigate(applyPath);
      } else {
        redirectToLogin();
      }
    }
  }, [
    data,
    error,
    isLoggedIn,
    isLoadingApplicationType,
    isRedirecting,
    navigate,
    getSearchParameter,
    jobSlug,
    isLoadingNeffi,
    isLoadingCvs,
    isLoadingUserPhone,
    normalizedNeffiScore,
    redirectToLogin,
    isApplyClickInProgress,
    resetAndShowError,
    applicationType,
    isXingApplyFlow,
    isRequiredUserPhoneMissingOrInvalid,
    userRecentCvs.length,
  ]);

  const onClick = useCallback(
    (trackingProps?: Record<string, unknown>) => {
      trackApplyIntention(trackingProps);
      setIsApplyClickInProgress(true);
      getJobApplicationType({
        variables: { slug: jobSlug },
      });

      if (isLoggedIn && isXingApplyFlow) {
        getLazyNeffiScore();
        getUserRecentCvs();
      }
    },
    [
      getJobApplicationType,
      getLazyNeffiScore,
      getUserRecentCvs,
      isLoggedIn,
      isXingApplyFlow,
      jobSlug,
      trackApplyIntention,
    ]
  );

  const applyButtonState = useMemo(() => {
    return {
      icon: IconButterflyMono,
      to: undefined, // overwrites default LO fencing redirection
      onClick,
      loading:
        isLoadingApplicationType ||
        isLoadingNeffi ||
        isLoadingCvs ||
        isLoadingUserPhone ||
        isRedirecting,
    };
  }, [
    isLoadingApplicationType,
    isLoadingCvs,
    isLoadingNeffi,
    isLoadingUserPhone,
    isRedirecting,
    onClick,
  ]);

  return (
    <ApplyButtonStateContext.Provider value={applyButtonState}>
      {children}
    </ApplyButtonStateContext.Provider>
  );
};

export const useApplyButtonStateProps = (): InstantApplyButtonState => {
  return useContext(ApplyButtonStateContext);
};
